import React, { useState, useEffect, useMemo } from "react";
import UpdateControlSlide from "../../services/event/set-speaker-control";
import { toast } from "react-toastify";  // Import toast from react-toastify
import "react-toastify/dist/ReactToastify.css";  // Import styles for react-toastify
import "./speakerlist.css";
import { generateClient } from "aws-amplify/api";
import { toggleSlideControl } from "../../graphql/mutations";
import * as config from "../../aws-exports";
const client = generateClient(config);

interface SpeakerDropdownProps {
  speakerBio: any[];
  meetingNumber: string;
}

const SpeakerDropdown: React.FC<SpeakerDropdownProps> = ({
  speakerBio,
  meetingNumber,
}) => {
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [showSpeakerDropdown, setShowSpeakerDropdown] = useState(false);
  const [speakerToggles, setSpeakerToggles] = useState<Record<string, boolean>>({});

  // Memoize filtered speakers to avoid unnecessary re-renders
  const filteredSpeakers = useMemo(
    () => speakerBio.filter((sp) => sp.role !== 1 && sp.setAsSpeaker === true),
    [speakerBio]
  );

  // Initialize speakerToggles based on slideControl property for filtered speakers
  useEffect(() => {
    const initialToggles = filteredSpeakers.reduce<Record<string, boolean>>(
      (acc, sp) => {
        acc[sp.email] = sp.slideControl || false;
        return acc;
      },
      {}
    );
    setSpeakerToggles(initialToggles);
  }, [filteredSpeakers]);

  // Helper to call API on toggle
  const handleToggle = async (checked: boolean, sp: any) => {
    const key = sp.email;
    setLoadingKey(key);
    setErrorKey(null);
    setSpeakerToggles((prev) => ({ ...prev, [key]: checked }));

    try {
      const res = await UpdateControlSlide(
        { setSlideControl: checked },
        meetingNumber,
        sp.email
      );
      if (res?.status === 200) {
        // Show distinct success toast for enable/disable
        toast.success(
          `${sp.name || sp.email} slide control ${
            checked ? "enabled" : "disabled"
          }!`
        );

        // Send to AppSync
        try {
          await client.graphql({
            query: toggleSlideControl,
            variables: {
              eventid: meetingNumber,
              enableSlideControl: checked,
              email: sp.email
            },
          });
        } catch (error) {
          console.error("Error toggling slide control:", error);
        }
      } else {
        setErrorKey(key);
        toast.error(
          `Failed to update slide control for ${sp.name || sp.email}.`
        );
      }
    } catch (e) {
      setErrorKey(key);
      toast.error(
        `Error: Unable to update slide control for ${sp.name || sp.email}.`
      );
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div className="dropdown-wrapper">
      <button
        style={{ marginLeft: 8 }}
        onClick={() => setShowSpeakerDropdown((prev) => !prev)}
        className="slide-control-btn"
        type="button"
      >
        Slide Control ▼
      </button>

      <div
        className={`dropdown-container ${
          showSpeakerDropdown ? "show" : ""
        }`}
      >
        <div className="dropdown-list">
          {filteredSpeakers.length === 0 ? (
            <div
              style={{ padding: 20, textAlign: "center", color: "#888" }}
            >
              No speakers found
            </div>
          ) : (
            filteredSpeakers.map((sp: any) => {
              const key = sp.id || sp.email || sp.name;
              return (
                <div key={key} className="dropdown-item">
                  <span
                    style={{ flex: 1, fontWeight: 600, color: "#222", fontSize: 15 }}
                  >
                    {sp.name || sp.email || key}
                  </span>

                  <label
                    className={`toggle-switch ${
                      loadingKey === key ? "loading" : ""
                    } ${speakerToggles[key] ? "on" : ""}`}
                    onClick={() =>
                      !loadingKey && handleToggle(!speakerToggles[key], sp)
                    }
                  >
                    <input
                      type="checkbox"
                      checked={!!speakerToggles[key]}
                      disabled={!!loadingKey}
                      style={{ display: "none" }}
                    />
                    <span className="toggle-circle" />
                  </label>

                  {errorKey === key && (
                    <span className="error-message">Error!</span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeakerDropdown;
